import useApi from "@/hooks/useApi";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Trigger, WorkflowListItem, triggerDefs } from "./types";
import { Checkbox, WorkflowItem } from "./WorkflowHelpers";
import useWorkflows from "@/hooks/useWorkflows";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useActivityLog from "@/hooks/useActivityLog";
import useTrackEvent from "@/hooks/useTrackEvent";
import { useNavigate } from "react-router-dom";
import BulkActionBar from "@/components/BulkActionBar";
import WorkflowsJourneyView from "./WorkflowsJourneyView";

type StatusFilter = "active" | "all" | "archived";
type GroupBy = "trigger" | "category" | "flat";

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "archived", label: "Archived" },
];

const GROUP_BY_STORAGE_KEY = "workflows.groupBy";

/**
 * Triggers where "priority" is meaningful (multiple workflows compete,
 * first-match-by-priority wins). Everything else just fires on the event
 * and priority is not a useful sort key.
 */
const PRIORITY_RELEVANT_TRIGGERS = new Set<string>([
  "conversation_opened",
  "widget_loaded",
  "chat_button_clicked",
]);

function isPriorityRelevant(triggerType: string): boolean {
  return PRIORITY_RELEVANT_TRIGGERS.has(triggerType);
}

function TriggerBadge({ trigger }: { trigger: Trigger }) {
  const label = triggerDefs[trigger] ?? trigger;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-600 border border-blue-100 tracking-wide">
      {label}
    </span>
  );
}

function RunModeBadge({ runMode }: { runMode?: string }) {
  if (!runMode || runMode === "exclusive") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-500 tracking-wide">
        Exclusive
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-violet-50 text-violet-600 border border-violet-100 tracking-wide">
      Always
    </span>
  );
}

export default function WorkflowsLandingV2() {
  const { workflows, setWorkflows, fetch } = useWorkflows();
  const api = useApi();
  const { track } = useActivityLog();
  const { track: trackEvent } = useTrackEvent();
  const { displayToast } = useToastAlert();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [reusableOpen, setReusableOpen] = useState(false);
  const [recentlyHiddenIds, setRecentlyHiddenIds] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<"list" | "journey">("list");
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    try {
      const v = localStorage.getItem(GROUP_BY_STORAGE_KEY);
      if (v === "trigger" || v === "category" || v === "flat") return v;
    } catch { /* ignore */ }
    return "trigger";
  });
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState<"name" | "trigger" | "modified" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    try { localStorage.setItem(GROUP_BY_STORAGE_KEY, groupBy); } catch { /* ignore */ }
  }, [groupBy]);

  useEffect(() => {
    fetch();
    trackEvent("feature_click", "Workflows/V2Landing/open");
  }, [fetch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, groupBy]);

  const templates = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => w.isTemplate) : []),
    [workflows]
  );
  const regularWorkflows = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => !w.isTemplate) : []),
    [workflows]
  );

  const statusFilteredWorkflows = useMemo(() => {
    return regularWorkflows.filter((w) => {
      const isArchived = w.workflowStatus === "deprecated";
      if (statusFilter === "active" && (isArchived || !w.enabled)) return false;
      if (statusFilter === "archived" && !isArchived) return false;
      return true;
    });
  }, [regularWorkflows, statusFilter]);

  const visibleWorkflows = useMemo(() => {
    let result = statusFilteredWorkflows;
    if (activeCategories.size > 0) {
      result = result.filter((w) => {
        const cat = (w.category && w.category.trim()) || "Uncategorized";
        return activeCategories.has(cat);
      });
    }
    if (searchQuery.trim()) {
      result = result.filter(w => w.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return result;
  }, [statusFilteredWorkflows, activeCategories, searchQuery]);

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    statusFilteredWorkflows.forEach((w) => {
      const cat = (w.category && w.category.trim()) || "Uncategorized";
      set.add(cat);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [statusFilteredWorkflows]);

  function toggleCategory(cat: string, additive: boolean) {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (additive) {
        if (next.has(cat)) next.delete(cat); else next.add(cat);
      } else {
        if (next.size === 1 && next.has(cat)) {
          next.clear();
        } else {
          next.clear();
          next.add(cat);
        }
      }
      return next;
    });
  }

  async function toggleWorkflow(id: number) {
    if (!workflows) return;
    const index = workflows.findIndex((w) => w.id === id);
    if (index === -1) return;
    const workflow = workflows[index];
    const wasEnabled = workflow.enabled;
    const clone = [...workflows];
    clone[index] = { ...workflow, enabled: !wasEnabled };
    setWorkflows(clone);
    if (wasEnabled) {
      setRecentlyHiddenIds((prev) => new Set([...prev, id]));
    } else {
      setRecentlyHiddenIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
    try {
      await api.messaging.post(`Workflows/toggle/${id}`);
    } catch {
      const reverted = [...clone];
      reverted[index] = { ...workflow, enabled: wasEnabled };
      setWorkflows(reverted);
      displayToast(Severity.Error, "Failed to update workflow status");
      return;
    }
    track({
      category: "Workflows",
      action: wasEnabled ? "WorkflowDisabled" : "WorkflowEnabled",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow.name
    });
  }

  async function removeWorkflow(id: number) {
    if (workflows === null) return;
    const workflow = workflows.find((w) => w.id === id);
    try {
      const resp = await api.messaging.delete(`Workflows/${id}`);
      if (resp.status === 409) {
        const body = await resp.json().catch(() => ({}));
        displayToast(Severity.Error, body?.error ?? "Cannot delete this template. Detach linked workflows first.");
        return;
      }
    } catch {
      displayToast(Severity.Error, "Failed to delete workflow");
      return;
    }
    setWorkflows(workflows?.filter((w) => w.id !== id));
    track({
      category: "Workflows",
      action: "WorkflowDeleted",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow?.name
    });
  }

  async function duplicateWorkflow(id: number) {
    try {
      const resp = await api.messaging.post(`Workflows/${id}/duplicate`, {});
      if (!resp.ok) { displayToast(Severity.Error, "Failed to duplicate workflow"); return; }
      const body = await resp.json();
      await fetch();
      displayToast(Severity.Success, `Workflow duplicated: "${body?.name ?? "copy"}" (disabled — enable when ready)`);
    } catch {
      displayToast(Severity.Error, "Failed to duplicate workflow");
    }
  }

  async function archiveWorkflow(id: number, replacedById?: number) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/status`, { status: "deprecated", replacedById: replacedById ?? null });
      if (!resp.ok) { displayToast(Severity.Error, "Failed to archive workflow"); return; }
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, workflowStatus: "deprecated", replacedById: replacedById ?? null, enabled: false } : w));
      if (statusFilter === "active") setRecentlyHiddenIds((prev) => new Set([...prev, id]));
      displayToast(Severity.Success, "Workflow archived");
    } catch {
      displayToast(Severity.Error, "Failed to archive workflow");
    }
  }

  async function unarchiveWorkflow(id: number) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/status`, { status: "active", replacedById: null });
      if (!resp.ok) { displayToast(Severity.Error, "Failed to unarchive workflow"); return; }
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, workflowStatus: null, replacedById: null, enabled: false } : w));
      displayToast(Severity.Success, "Workflow unarchived (disabled — enable when ready)");
    } catch {
      displayToast(Severity.Error, "Failed to unarchive workflow");
    }
  }

  async function detachWorkflow(id: number) {
    try {
      await api.messaging.post(`Workflows/detach/${id}`);
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, templateId: null, templateName: null } : w));
      displayToast(Severity.Success, "Workflow detached. You can now edit it independently.");
    } catch {
      displayToast(Severity.Error, "Failed to detach workflow from template");
    }
  }

  async function renameWorkflow(id: number, newName: string) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/name`, { name: newName });
      if (!resp.ok) { displayToast(Severity.Error, "Failed to rename workflow"); return; }
      setWorkflows(workflows.map((w) => w.id === id ? { ...w, name: newName } : w));
    } catch {
      displayToast(Severity.Error, "Failed to rename workflow");
    }
  }

  async function bulkArchive() {
    const ids = [...selectedIds];
    await Promise.all(ids.map((id) => api.messaging.patch(`Workflows/${id}/status`, { status: "deprecated", replacedById: null })));
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, workflowStatus: "deprecated", enabled: false } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} archived.`);
  }

  async function bulkEnable() {
    const ids = [...selectedIds];
    for (const id of ids) await api.messaging.post(`Workflows/toggle/${id}`);
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, enabled: true } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} enabled.`);
  }

  async function bulkDisable() {
    const ids = [...selectedIds];
    for (const id of ids) await api.messaging.post(`Workflows/toggle/${id}`);
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, enabled: false } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} disabled.`);
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll(ids: number[]) {
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  function renderPriorityBanner() {
    return (
      <div className="mx-6 mt-3 mb-0 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-xs text-amber-800">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>
          <span className="font-semibold text-amber-900">Execution order: </span>
          Priority Rules → Workflows (first match by priority) → Always-run Workflows → Automation/AI Rules.
          A routing Rule only fires if no Workflow matched.
        </span>
      </div>
    );
  }

  function renderTopbar() {
    return (
      <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-200 bg-white">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Workflows</h1>
          <p className="text-xs text-slate-400 mt-0.5">Automate conversation routing, messaging, and triggers</p>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-slate-100 rounded-md p-0.5 gap-0.5" role="tablist" aria-label="View mode">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "list"}
            onClick={() => setViewMode("list")}
            className={`text-xs px-2.5 py-1 rounded transition-all font-medium ${
              viewMode === "list" ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              List
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "journey"}
            onClick={() => setViewMode("journey")}
            className={`text-xs px-2.5 py-1 rounded transition-all font-medium ${
              viewMode === "journey" ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="5" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="19" cy="19" r="2" />
                <path d="M12 7v4M7 17l5-6M17 17l-5-6" />
              </svg>
              Journey
            </span>
          </button>
        </div>

        {/* Settings */}
        <button
          type="button"
          onClick={() => { trackEvent("feature_click", "Workflows/SettingsHub/open"); navigate("/settings/hub"); }}
          title="Workflow Settings"
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>

        {/* Create */}
        <button
          type="button"
          onClick={() => navigate("/workflows/create")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 active:opacity-80 transition-opacity shadow-sm"
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Workflow
        </button>
      </div>
    );
  }

  function renderFilterRow() {
    const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
      { value: "trigger", label: "Trigger" },
      { value: "category", label: "Category" },
      { value: "flat", label: "Flat" },
    ];
    return (
      <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-2.5">
        <div className="flex items-center gap-3 flex-wrap">

          {/* Status filter */}
          <div className="flex items-center bg-white rounded-md border border-slate-200 p-0.5 gap-0.5 shrink-0">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={`text-xs px-3 py-1 rounded transition-all font-medium ${
                  statusFilter === tab.value
                    ? "bg-[var(--color-primary)] text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Group-by */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Group</span>
            <div className="flex items-center bg-white rounded-md border border-slate-200 p-0.5 gap-0.5">
              {GROUP_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setGroupBy(o.value)}
                  className={`text-xs px-2.5 py-1 rounded transition-all font-medium ${
                    groupBy === o.value
                      ? "bg-slate-700 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="relative shrink-0">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="text-xs pl-7 pr-3 py-1.5 rounded-md border border-slate-200 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)]/60 transition-all w-44"
            />
          </div>

          <div className="flex-1" />

          {/* Count */}
          <span className="text-xs text-slate-400 tabular-nums">
            {visibleWorkflows.length} {visibleWorkflows.length === 1 ? "workflow" : "workflows"}
          </span>
        </div>

        {/* Category chip row */}
        {availableCategories.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto">
            <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider shrink-0">Filter</span>
            <button
              type="button"
              onClick={() => setActiveCategories(new Set())}
              className={`shrink-0 px-2.5 py-0.5 text-xs rounded-full border transition-all font-medium ${
                activeCategories.size === 0
                  ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              All
            </button>
            {availableCategories.map((cat) => {
              const active = activeCategories.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={(e) => toggleCategory(cat, e.metaKey || e.ctrlKey || e.shiftKey)}
                  title="Click to filter · Cmd/Ctrl-click for multiple"
                  className={`shrink-0 px-2.5 py-0.5 text-xs rounded-full border transition-all font-medium ${
                    active
                      ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderConditionLabel(w: WorkflowListItem) {
    if (w.conditionSummary) return <span className="text-xs text-slate-500 max-w-[180px] truncate block">{w.conditionSummary}</span>;
    return <span className="text-xs text-slate-300 italic">Always</span>;
  }

  function sortSection(items: WorkflowListItem[], sectionUsesPriority: boolean): WorkflowListItem[] {
    const copy = [...items];
    if (sortCol) {
      const dir = sortDir === "asc" ? 1 : -1;
      copy.sort((a, b) => {
        if (sortCol === "name") return dir * a.name.localeCompare(b.name);
        if (sortCol === "trigger") return dir * (a.triggerType ?? "").localeCompare(b.triggerType ?? "");
        if (sortCol === "modified") {
          const am = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const bm = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          return dir * (am - bm);
        }
        return 0;
      });
      return copy;
    }
    if (sectionUsesPriority) {
      copy.sort((a, b) => {
        const ap = isPriorityRelevant(a.triggerType);
        const bp = isPriorityRelevant(b.triggerType);
        if (ap && bp) {
          const apri = a.routingPriority ?? Number.MAX_SAFE_INTEGER;
          const bpri = b.routingPriority ?? Number.MAX_SAFE_INTEGER;
          if (apri !== bpri) return apri - bpri;
          return a.id - b.id;
        }
        if (ap) return -1;
        if (bp) return 1;
        return a.name.localeCompare(b.name);
      });
    } else {
      copy.sort((a, b) => a.name.localeCompare(b.name));
    }
    return copy;
  }

  function handleSortCol(col: "name" | "trigger" | "modified") {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  function SortIndicator({ col }: { col: "name" | "trigger" | "modified" }) {
    if (sortCol !== col) return (
      <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 text-slate-300" aria-hidden="true">
        <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
      </svg>
    );
    return (
      <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-1 text-[var(--color-primary)]" aria-hidden="true">
        {sortDir === "asc"
          ? <path d="M12 19V5M5 12l7-7 7 7" />
          : <path d="M12 5v14M5 12l7 7 7-7" />
        }
      </svg>
    );
  }

  function renderEmpty() {
    const hasFilters = searchQuery.trim() || activeCategories.size > 0 || statusFilter !== "active";
    return (
      <div className="py-16 flex flex-col items-center justify-center gap-4 text-slate-400">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
          </svg>
        </div>
        {hasFilters ? (
          <>
            <div className="text-center">
              <p className="text-sm font-medium text-slate-600">No workflows match these filters</p>
              <p className="text-xs text-slate-400 mt-1">Try adjusting your search or filter criteria</p>
            </div>
            <button
              type="button"
              onClick={() => { setSearchQuery(""); setActiveCategories(new Set()); setStatusFilter("active"); }}
              className="text-xs text-[var(--color-primary)] hover:opacity-80 underline"
            >
              Clear filters
            </button>
          </>
        ) : (
          <>
            <div className="text-center">
              <p className="text-sm font-medium text-slate-600">No workflows yet</p>
              <p className="text-xs text-slate-400 mt-1">Create your first workflow to start automating conversations</p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/workflows/create")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create Workflow
            </button>
          </>
        )}
      </div>
    );
  }

  function renderSectionTable(
    items: WorkflowListItem[],
    opts: { showPriorityColumn: boolean; keyPrefix: string }
  ) {
    const allIds = items.map((w) => w.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    return (
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 h-9 bg-slate-50/60">
            <th className="w-10 pl-3">
              <Checkbox checked={allSelected} onChange={() => selectAll(allIds)} />
            </th>
            <th className="pl-2 w-1/4 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              <button type="button" onClick={() => handleSortCol("name")} className="flex items-center gap-0.5 hover:text-slate-600 transition-colors">
                Name<SortIndicator col="name" />
              </button>
            </th>
            <th className="w-1/5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Condition</th>
            <th className="w-1/6 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              <button type="button" onClick={() => handleSortCol("trigger")} className="flex items-center gap-0.5 hover:text-slate-600 transition-colors">
                Trigger<SortIndicator col="trigger" />
              </button>
            </th>
            <th className="w-24 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Mode</th>
            {opts.showPriorityColumn && <th className="w-20 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Priority</th>}
            <th className="w-24 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
            <th className="w-16 pr-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w, i) => (
            <tr
              key={`${opts.keyPrefix}-${w.id}`}
              className={`group border-b border-slate-100 hover:bg-slate-50/70 transition-colors ${i === items.length - 1 ? "border-b-0" : ""}`}
            >
              <td className="pl-3 py-2.5">
                <Checkbox checked={selectedIds.has(w.id)} onChange={() => toggleSelect(w.id)} />
              </td>
              <td className="pl-2 py-2.5">
                <button
                  type="button"
                  onClick={() => navigate(`/workflows/${w.id}`)}
                  className="text-sm font-medium text-slate-700 hover:text-[var(--color-primary)] transition-colors text-left leading-tight"
                >
                  {w.name}
                </button>
                {w.lastModified && (
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {new Date(w.lastModified).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                )}
              </td>
              <td className="py-2.5 pr-2">{renderConditionLabel(w)}</td>
              <td className="py-2.5"><TriggerBadge trigger={w.triggerType} /></td>
              <td className="py-2.5"><RunModeBadge runMode={w.runMode} /></td>
              {opts.showPriorityColumn && (
                <td className="py-2.5">
                  {isPriorityRelevant(w.triggerType) ? (
                    <span className="text-xs text-slate-500 font-mono tabular-nums">
                      #{w.routingPriority != null ? w.routingPriority : "–"}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-300 italic">on event</span>
                  )}
                </td>
              )}
              <td className="py-2.5">
                <WorkflowItem
                  item={w}
                  isLast={i === items.length - 1}
                  isSelected={selectedIds.has(w.id)}
                  onSelect={() => toggleSelect(w.id)}
                  toggle={() => toggleWorkflow(w.id)}
                  remove={() => removeWorkflow(w.id)}
                  duplicate={() => duplicateWorkflow(w.id)}
                  onDetach={w.templateId ? () => detachWorkflow(w.id) : undefined}
                  onArchive={w.workflowStatus !== "deprecated" ? (replacedById) => archiveWorkflow(w.id, replacedById) : undefined}
                  onUnarchive={w.workflowStatus === "deprecated" ? () => unarchiveWorkflow(w.id) : undefined}
                  onRename={renameWorkflow}
                  renderStatusToggleOnly
                />
              </td>
              <td className="py-2.5 pr-3 text-right">
                <WorkflowItem
                  item={w}
                  isLast={i === items.length - 1}
                  isSelected={selectedIds.has(w.id)}
                  onSelect={() => toggleSelect(w.id)}
                  toggle={() => toggleWorkflow(w.id)}
                  remove={() => removeWorkflow(w.id)}
                  duplicate={() => duplicateWorkflow(w.id)}
                  onDetach={w.templateId ? () => detachWorkflow(w.id) : undefined}
                  onArchive={w.workflowStatus !== "deprecated" ? (replacedById) => archiveWorkflow(w.id, replacedById) : undefined}
                  onUnarchive={w.workflowStatus === "deprecated" ? () => unarchiveWorkflow(w.id) : undefined}
                  onRename={renameWorkflow}
                  renderActionsOnly
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderSectionHeading(
    label: string,
    count: number,
    sortLabel: string,
    opts?: { onAdd?: () => void }
  ) {
    return (
      <div className="flex items-center gap-2 px-6 mt-6 mb-2">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">{label}</span>
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500 tabular-nums">{count}</span>
        <span className="text-[11px] text-slate-300">·</span>
        <span className="text-[11px] text-slate-400 italic">{sortLabel}</span>
        <div className="flex-1 h-px bg-slate-100 ml-2" />
        {opts?.onAdd && (
          <button
            type="button"
            onClick={opts.onAdd}
            className="text-xs text-[var(--color-primary)] hover:opacity-75 transition-opacity font-medium shrink-0"
          >
            + Add
          </button>
        )}
      </div>
    );
  }

  function renderSections() {
    const items = visibleWorkflows;
    if (items.length === 0) return renderEmpty();

    if (groupBy === "flat") {
      const sectionUsesPriority = items.some((w) => isPriorityRelevant(w.triggerType));
      const sorted = sortSection(items, sectionUsesPriority);
      return (
        <div className="bg-white border border-slate-200 rounded-lg mx-6 mt-4 overflow-hidden shadow-sm">
          {renderSectionTable(sorted, { showPriorityColumn: sectionUsesPriority, keyPrefix: "flat" })}
        </div>
      );
    }

    const groups = new Map<string, WorkflowListItem[]>();
    if (groupBy === "trigger") {
      const order = Object.keys(triggerDefs);
      order.forEach((t) => {
        const group = items.filter((w) => w.triggerType === t);
        if (group.length > 0) groups.set(t, group);
      });
      items.forEach((w) => {
        if (!order.includes(w.triggerType)) {
          const existing = groups.get(w.triggerType) ?? [];
          existing.push(w);
          groups.set(w.triggerType, existing);
        }
      });
    } else {
      items.forEach((w) => {
        const cat = (w.category && w.category.trim()) || "Uncategorized";
        const existing = groups.get(cat) ?? [];
        existing.push(w);
        groups.set(cat, existing);
      });
    }

    const entries = Array.from(groups.entries());
    if (groupBy === "category") {
      entries.sort(([a], [b]) => {
        if (a === "Uncategorized") return 1;
        if (b === "Uncategorized") return -1;
        return a.localeCompare(b);
      });
    }

    return (
      <div className="pb-2">
        {entries.map(([key, group]) => {
          const sectionUsesPriority = group.some((w) => isPriorityRelevant(w.triggerType));
          const sorted = sortSection(group, sectionUsesPriority);
          const label = groupBy === "trigger"
            ? (triggerDefs[key as Trigger] ?? key)
            : key;
          const sortLabel = sortCol ? `Sorted by ${sortCol}` : sectionUsesPriority ? "by priority" : "A–Z";
          return (
            <div key={key}>
              {renderSectionHeading(label, group.length, sortLabel, {
                onAdd: () => navigate("/workflows/create"),
              })}
              <div className="bg-white border border-slate-200 rounded-lg mx-6 overflow-hidden shadow-sm">
                {renderSectionTable(sorted, { showPriorityColumn: sectionUsesPriority, keyPrefix: key })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderReusableFlows() {
    if (templates.length === 0) return null;
    return (
      <div className="px-6 mt-6 mb-4">
        <button
          type="button"
          onClick={() => setReusableOpen((o) => !o)}
          className="flex items-center gap-2 group"
        >
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest group-hover:text-slate-500 transition-colors">Reusable Flows</span>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-violet-100 text-[10px] font-semibold text-violet-600 tabular-nums">{templates.length}</span>
          </div>
          <div className="flex-1 h-px bg-slate-100" />
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`text-slate-400 transition-transform ${reusableOpen ? "rotate-180" : ""}`} aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {reusableOpen && (
          <div className="mt-2 rounded-lg border border-violet-100 bg-violet-50/50 overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 text-xs text-slate-500 border-b border-violet-100 bg-violet-50">
              Call these from any workflow using a <strong className="font-semibold">Call Workflow</strong> node — shared greeting scripts, data lookups, and escalation flows.
            </div>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-violet-100 h-9 bg-violet-50/40">
                  <th className="pl-4 w-1/3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="w-1/4 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Trigger</th>
                  <th className="w-24 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="w-20 pr-4 text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, i) => (
                  <tr key={t.id} className={`border-b border-violet-100 hover:bg-violet-100/30 transition-colors ${i === templates.length - 1 ? "border-b-0" : ""}`}>
                    <td className="pl-4 py-2.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/workflows/${t.id}`)}
                        className="text-sm font-medium text-slate-700 hover:text-violet-700 transition-colors"
                      >
                        {t.name}
                      </button>
                    </td>
                    <td className="py-2.5"><TriggerBadge trigger={t.triggerType} /></td>
                    <td className="py-2.5">
                      <WorkflowItem
                        item={t}
                        isLast={i === templates.length - 1}
                        toggle={() => toggleWorkflow(t.id)}
                        remove={() => removeWorkflow(t.id)}
                        duplicate={() => duplicateWorkflow(t.id)}
                        onRename={renameWorkflow}
                        renderStatusToggleOnly
                      />
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <WorkflowItem
                        item={t}
                        isLast={i === templates.length - 1}
                        toggle={() => toggleWorkflow(t.id)}
                        remove={() => removeWorkflow(t.id)}
                        duplicate={() => duplicateWorkflow(t.id)}
                        onRename={renameWorkflow}
                        renderActionsOnly
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  function renderBulkBar() {
    if (selectedIds.size === 0) return null;
    return (
      <div className="px-6 pt-3">
        <BulkActionBar>
          <span className="font-medium text-sm">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <button type="button" onClick={bulkEnable} className="px-3 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium transition-colors">Enable</button>
          <button type="button" onClick={bulkDisable} className="px-3 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium transition-colors">Disable</button>
          <button type="button" onClick={bulkArchive} className="px-3 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium transition-colors">Archive</button>
          <button type="button" onClick={() => setSelectedIds(new Set())} className="px-2 py-1 rounded-md border border-transparent hover:bg-slate-100 text-slate-400 text-xs transition-colors">Clear</button>
        </BulkActionBar>
      </div>
    );
  }

  return (
    <div className="pb-10 min-h-0">
      {renderTopbar()}
      {viewMode === "journey" ? (
        <WorkflowsJourneyView />
      ) : (
        <>
          {renderPriorityBanner()}
          {renderFilterRow()}
          {renderBulkBar()}
          {renderSections()}
          {renderReusableFlows()}
        </>
      )}
    </div>
  );
}
